import type { ApplicationStatusResponse } from "@credit-acceptance/step6-bff-client";

import { getDealStateVerdictForSection } from "./dealState/parseDealState";
import { SECTION_IDS, type SectionCapability, type WorkbenchRoleCapabilities, type WorkbenchSectionId } from "./types";

/** Why a section ended up hidden or read-only — surfaced for banners/tooltips/debugging. */
export type PermissionDenialReason =
  | "ROLE_NOT_VISIBLE"
  | "DEAL_STATE_HIDDEN"
  | "VLA_LOCKED_BY_ANOTHER_USER"
  | "DEAL_STATE_NOT_EDITABLE"
  | "ROLE_READ_ONLY";

/** Final, ready-to-render verdict for one Step6 section. */
export interface EffectiveSectionPermission {
  visible: boolean;
  editable: boolean;
  /** Present whenever `editable` is false (or `visible` is false) — omitted when fully editable. */
  reason?: PermissionDenialReason;
}

export interface ResolveSectionPermissionInput {
  role: SectionCapability;
  /** Whether deal state (status/phase) allows this section to render at all — see `getDealStateVerdictForSection`. */
  dealStateVisible: boolean;
  /** Whether deal state (status/phase) allows editing — see `getDealStateVerdictForSection`. */
  dealStateEditable: boolean;
  /** True when the current user holds the VLA in-use lock for this deal. */
  isVlaHeldByMe: boolean;
}

/**
 * Combines the role, deal-state, and VLA-lock layers into one final
 * visible/editable verdict for a single section, per the required precedence:
 * **VLA Lock → Status/Phase → Role**. That order only changes which
 * `reason` is reported when more than one gate fails — the `editable`
 * boolean itself is always the AND of every gate, order-independent.
 *
 * Visibility is resolved first and independently (a hidden section can't be
 * "editable" regardless of lock/status/role), then the lock → deal-state →
 * role precedence applies to the editability verdict.
 * @param {ResolveSectionPermissionInput} input - The three permission layers' verdicts for one section.
 * @returns {EffectiveSectionPermission} The combined, ready-to-render verdict.
 */
export function resolveSectionPermission({
  role,
  dealStateVisible,
  dealStateEditable,
  isVlaHeldByMe,
}: ResolveSectionPermissionInput): EffectiveSectionPermission {
  const visible = role.visible && dealStateVisible;
  if (!visible) {
    return { visible: false, editable: false, reason: role.visible ? "DEAL_STATE_HIDDEN" : "ROLE_NOT_VISIBLE" };
  }

  // Precedence for the read-only reason: VLA Lock -> Status/Phase -> Role.
  if (!isVlaHeldByMe) {
    return { visible: true, editable: false, reason: "VLA_LOCKED_BY_ANOTHER_USER" };
  }
  if (!dealStateEditable) {
    return { visible: true, editable: false, reason: "DEAL_STATE_NOT_EDITABLE" };
  }
  if (role.forcedReadOnly || !role.writable) {
    return { visible: true, editable: false, reason: "ROLE_READ_ONLY" };
  }

  return { visible: true, editable: true };
}

/**
 * Runs {@link resolveSectionPermission} for every Step6 section, deriving each section's
 * deal-state visible/editable inputs from {@link getDealStateVerdictForSection},
 * which already knows which matrix primitive applies to which section (sections
 * with no deal-state rule simply get `{ visible: true, editable: true }`
 * back, so only role + lock decide their verdict).
 * @param {WorkbenchRoleCapabilities} role - Per-section role capabilities from `parseRolePermissions`.
 * @param {ApplicationStatusResponse | undefined} status - The deal's current status/phase.
 * @param {boolean} isVlaHeldByMe - Whether the current user holds the VLA lock.
 * @returns {Record<WorkbenchSectionId, EffectiveSectionPermission>} The final verdict for all six sections.
 */
export function resolveAllSectionPermissions(
  role: WorkbenchRoleCapabilities,
  status: ApplicationStatusResponse | undefined,
  isVlaHeldByMe: boolean
): Record<WorkbenchSectionId, EffectiveSectionPermission> {
  return Object.fromEntries(
    SECTION_IDS.map((sectionId) => {
      const dealState = getDealStateVerdictForSection(sectionId, status);

      return [
        sectionId,
        resolveSectionPermission({
          role: role.sections[sectionId],
          dealStateVisible: dealState.visible,
          dealStateEditable: dealState.editable,
          isVlaHeldByMe,
        }),
      ];
    })
  ) as Record<WorkbenchSectionId, EffectiveSectionPermission>;
}
